/** @format */

// src/FrontendDashboard.js
import { useState, useEffect } from '@wordpress/element';
import AthleteList from './components/AthleteList.js';
import RelayList from './components/RelayList.js';
import EntriesArchive from './components/EntriesArchive.js'; // Imported new separated component

const FrontendDashboard = () => {
	const [activeSubTab, setActiveSubTab] = useState('athletes');
	const [athletes, setAthletes] = useState([]);
	const [relays, setRelays] = useState([]); // Shared local state cache hook
	const [archiveEntries, setArchiveEntries] = useState([]);
	const [meta, setMeta] = useState({ clubs: {}, countries: {} });

	const [ageStandard, setAgeStandard] = useState('standard_hs');

	const [loading, setLoading] = useState(true);
	const [archiveLoading, setArchiveLoading] = useState(false);

	const getGlobalRoutingData = () => {
		if (typeof window !== 'undefined') {
			if (window.ammData) return window.ammData;
			if (window.wp?.ajax?.settings?.ammData) return window.wp.ajax.settings.ammData;
		}
		return null;
	};

	// Parallel prefetch pipeline that populates both individual lists and relay collections concurrently
	const prefetchDashboardData = async () => {
		const globalRouting = getGlobalRoutingData();
		if (!globalRouting?.api_url) {
			setLoading(false);
			return;
		}

		setLoading(true);
		const headers = { 'X-WP-Nonce': globalRouting.nonce };

		try {
			const [athletesRes, relaysRes, metaRes] = await Promise.all([fetch(`${globalRouting.api_url}/athletes`, { headers }), fetch(`${globalRouting.api_url}/relays`, { headers }), fetch(`${globalRouting.api_url}/meta`, { headers })]);

			const athletesData = athletesRes.ok ? await athletesRes.json() : [];
			const relaysData = relaysRes.ok ? await relaysRes.json() : [];
			const metaData = metaRes.ok ? await metaRes.json() : { clubs: {}, countries: {} };

			setAthletes(Array.isArray(athletesData) ? athletesData : []);
			setRelays(Array.isArray(relaysData) ? relaysData : []);

			if (metaData) {
				setMeta(metaData);
				if (metaData.ageStandard) {
					setAgeStandard(metaData.ageStandard);
				}
			}
		} catch (err) {
			console.error('Prefetch Pipeline Failure Exception context:', err);
		} finally {
			setLoading(false);
		}
	};

	const fetchEntriesArchive = () => {
		const globalRouting = getGlobalRoutingData();
		if (!globalRouting?.api_url || archiveEntries.length > 0) return; // Cache protection guard rule

		setArchiveLoading(true);
		fetch(`${globalRouting.api_url}/entries-archive`, {
			headers: { 'X-WP-Nonce': globalRouting.nonce },
		})
			.then((res) => {
				if (!res.ok) throw new Error('Historical database transmission tracking failed.');
				return res.json();
			})
			.then((data) => {
				setArchiveEntries(Array.isArray(data) ? data : []);
				setArchiveLoading(false);
			})
			.catch((err) => {
				console.error('Fetch Archive Error:', err);
				setArchiveLoading(false);
			});
	};

	// Initialize the synchronous background download and coordinate historical state synchronization on mount
	useEffect(() => {
		prefetchDashboardData();

		// Sync local navigation layout context matching browser address properties via 'panel' query parameter
		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search);
			const initialPanel = params.get('panel');
			const validPanels = ['athletes', 'relays', 'archive'];

			if (initialPanel && validPanels.includes(initialPanel)) {
				setActiveSubTab(initialPanel);
				// Pipeline fallback option if user deep links straight into the archive panel on refresh
				if (initialPanel === 'archive') {
					fetchEntriesArchive();
				}
			}
		}
	}, []);

	// Intercept active tab transformations and patch the browser address architecture history using 'panel'
	const handleTabChange = (targetTab) => {
		setActiveSubTab(targetTab);

		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search);
			params.set('panel', targetTab);

			const dynamicPathQuery = `${window.location.pathname}?${params.toString()}`;
			window.history.replaceState({ path: dynamicPathQuery }, '', dynamicPathQuery);

			// Fetch the historical entries dataset instantly if transitioning to the archive sub-tab
			if (targetTab === 'archive') {
				fetchEntriesArchive();
			}
		}
	};

	return (
		<div className="container-fluid py-4 px-0 advanced-mm-dashboard-wrapper">
			<div className="d-flex justify-content-between align-items-center mb-4">
				<div>
					<h2 className="fw-bold text-dark mb-1">Dashboard</h2>
					<p className="text-muted small mb-0">
						Configure your team members, relay rosters, and audit ongoing event entry archives rule-matched to
						<span className="badge bg-secondary ms-1 lh-sm text-uppercase">{ageStandard === 'standard_hs' ? 'ISSA High School Rules' : 'WA International Rules'}</span>
					</p>
				</div>
			</div>

			{/* Sub-Navigation Dashboard Interface Tabs */}
			<ul className="nav nav-tabs border-bottom-0 mb-3">
				<li className="nav-item">
					<button className={`nav-link fw-semibold px-4 ${activeSubTab === 'athletes' ? 'active text-white bg-primary border border-primary border-3 fw-bold' : 'text-secondary border border-3'}`} onClick={() => handleTabChange('athletes')}>
						<i className="fas fa-user me-2"></i>Athletes
					</button>
				</li>
				<li className="nav-item">
					<button className={`nav-link fw-semibold px-4 ${activeSubTab === 'relays' ? 'active text-white bg-primary border border-primary border-3 fw-bold' : 'text-secondary border border-3'}`} onClick={() => handleTabChange('relays')}>
						<i className="fas fa-users me-2"></i>Relay Teams
					</button>
				</li>
				<li className="nav-item">
					<button className={`nav-link fw-semibold px-4 ${activeSubTab === 'archive' ? 'active text-white bg-primary border border-primary border-3 fw-bold' : 'text-secondary border border-3'}`} onClick={() => handleTabChange('archive')}>
						<i className="fas fa-archive me-2"></i>Entries Archive
					</button>
				</li>
			</ul>

			{/* Tab Rendering Router Panels */}
			{activeSubTab === 'athletes' &&
				(loading ? (
					<div className="text-center py-5">
						<div className="spinner-border text-primary" role="status"></div>
						<p className="text-muted small mt-2">Loading athletic roster records...</p>
					</div>
				) : (
					<AthleteList athletes={athletes} meta={meta} ageStandard={ageStandard} onRefresh={prefetchDashboardData} />
				))}

			{activeSubTab === 'relays' &&
				(loading ? (
					<div className="text-center py-5">
						<div className="spinner-border text-primary" role="status"></div>
						<p className="text-muted small mt-2">Recovering team structures...</p>
					</div>
				) : (
					<RelayList relays={relays} ageStandard={ageStandard} onRefresh={prefetchDashboardData} />
				))}

			{activeSubTab === 'archive' && <EntriesArchive archiveEntries={archiveEntries} archiveLoading={archiveLoading} onFetchArchive={fetchEntriesArchive} />}
		</div>
	);
};

export default FrontendDashboard;
